import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, MessageSquare } from "lucide-react";

export default function AdminTestimonials() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    author_name: "", author_location: "", rating: "5", text: "", source: "Google"
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = () => {
    axios.get(`${API}/testimonials`).then(r => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchItems(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ author_name: "", author_location: "", rating: "5", text: "", source: "Google" });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      author_name: item.author_name,
      author_location: item.author_location || "",
      rating: String(item.rating || 5),
      text: item.text,
      source: item.source || "Google"
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.author_name || !form.text) { toast.error("Name and review text required"); return; }
    const payload = {
      author_name: form.author_name,
      author_location: form.author_location,
      rating: parseInt(form.rating),
      text: form.text,
      source: form.source,
      is_verified: true
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/testimonials/${editing.id}`, payload, { headers });
        toast.success("Testimonial updated");
      } else {
        await axios.post(`${API}/admin/testimonials`, payload, { headers });
        toast.success("Testimonial created");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/testimonials/${deleteId}`, { headers });
      toast.success("Testimonial deleted");
      setDeleteId(null);
      fetchItems();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-testimonials-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#0D0D0D]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Testimonials</h1>
          <p className="text-sm text-[#4B5563]">Manage customer reviews and testimonials</p>
        </div>
        <Button onClick={openCreate} className="bg-[#0D0D0D] text-white hover:bg-[#1a1a1a] gap-2" data-testid="admin-testimonials-add-button">
          <Plus className="w-4 h-4" /> Add Review
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="p-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-[#4B5563] mb-3">No testimonials yet</p>
            <Button onClick={openCreate} variant="outline" className="gap-2"><Plus className="w-4 h-4" /> Add Your First Review</Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-gray-100">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Author</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead className="hidden sm:table-cell">Review</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{item.author_name}</p>
                        <p className="text-xs text-[#4B5563]">{item.author_location}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-0.5">
                        {[...Array(item.rating || 5)].map((_, i) => (
                          <Star key={i} className="w-3 h-3 fill-[#F59E0B] text-[#F59E0B]" />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell max-w-xs">
                      <p className="text-xs text-[#4B5563] line-clamp-2">"{item.text}"</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{item.source}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(item)}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => setDeleteId(item.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Testimonial" : "Add Testimonial"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Author Name *</Label>
                <Input value={form.author_name} onChange={e => setForm(p => ({...p, author_name: e.target.value}))} placeholder="John D." />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={form.author_location} onChange={e => setForm(p => ({...p, author_location: e.target.value}))} placeholder="Vero Beach, FL" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rating</Label>
                <Select value={form.rating} onValueChange={v => setForm(p => ({...p, rating: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[5,4,3,2,1].map(r => <SelectItem key={r} value={String(r)}>{r} Star{r > 1 ? 's' : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Source</Label>
                <Select value={form.source} onValueChange={v => setForm(p => ({...p, source: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Google", "Yelp", "Facebook", "Website"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Review Text *</Label>
              <Textarea value={form.text} onChange={e => setForm(p => ({...p, text: e.target.value}))} placeholder="Write the review..." rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#0D0D0D] text-white hover:bg-[#1a1a1a]">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Testimonial</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove this testimonial.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
